/**
 * Rule builder provides methods to build rules or parts of rules.
 * E.g. rule_builder.aggregate returns an object that describes an aggregator.
 *
 * @module
 */
var rule_builder = exports;

var _ = require('optimizely-client-fn');
var behavior:any = {
  FIELDS: require('core/behavior/enums').FIELDS,
  FIELDS_V0_2: require('core/behavior/enums').FIELDS_V0_2,
};
var JSONEnv = require('core/lib/json');
var Logger = require('core/utils/logger');
var rules = require('./rules');

/**
 * Milliseconds in 1 day.
 * @const
 * @type {number}
 */
rule_builder.MILLIS_IN_A_DAY = 24 * 60 * 60 * 1000;

/**
 * Returns the field descriptor for an aggregated field.
 * @param {string} aggregateOp Aggregate operator.
 * @param {(string|Array.<string>)} [field] Field descriptor.
 * @returns {Array.<string>} Field descriptor.
 */
rule_builder.aggregateField = function(aggregateOp:string, field?:string|string[]): string[] {
  if (_.isString(field)) {
    field = [ <string>field ];
  }
  field = field || rules.DEFAULT_FIELD;
  return [ rules.generateAlias(aggregateOp, field) ];
};

/**
 * Returns the field descriptor for a grouped field.
 * @param {(string|Array.<string>)} [field] Field descriptor.
 * @returns {Array.<string>} Field descriptor.
 */
rule_builder.groupField = function(field?:string|string[]): string[] {
  if (_.isString(field)) {
    field = [ field as string ];
  }
  field = (field || rules.DEFAULT_FIELD) as string[];
  return [ field.join('.') ];
};

/**
 * Map to canonical operators.
 * @private
 * @type {Object.<string>}
 */
var OP_MAP = {
  '<': 'lt',
  '<=': 'lte',
  '>': 'gt',
  '>=': 'gte',
  '=': 'eq',
  '==': 'eq',
};

/**
 * Returns canonical operator.
 * @param {string} op
 * @returns {string} Canonical operator.
 * @private
 */
function canonicalizeOperator(op:string): string {
  op = (op || '').toString().trim();
  return OP_MAP[op] || op;
}

/**
 * Returns an object that describes a comparison of a field value with the
 * input value.
 * @param {string} op Comparison operation.
 * @param {string|Array.<string>} field Field descriptor.
 * @param {*} value Value.
 * @returns {Object.<*>} Object describing operation.
 */
rule_builder.fieldComparison = function(op:string, field:string|string[], value:any): Record<string, any> {
  op = canonicalizeOperator(op);
  if (_.isString(field)) {
    field = [ field as string ];
  }
  if (op === 'exists') {
    // We can't return the operand {'value': undefined}, because that is rejected by the type invariants.
    return {
      'op': op,
      'args': [{
        'field': field
      }]
    };
  } else {
    return {
      'op': op,
      'args': [{
        'field': field
      }, {
        'value': value
      }]
    };
  }
};

/**
 * Returns an object that describes a comparison of the relative
 * time with the input number of days.
 * @param {string} op Comparison operation.
 * @param {number} days Number of days.
 * @returns {Object.<*>} Object that describes time comparison.
 */
rule_builder.relativeTimeComparison = function(op:string, days:number): Record<string, any> {
  return {
    'op': canonicalizeOperator(op),
    'args': [{
      'op': '-',
      'args': [{
        'eval': 'now'
      }, {
        'field': [ behavior.FIELDS.TIME ]
      }]
    }, {
      'value': days * rule_builder.MILLIS_IN_A_DAY
    }]
  };
};

/**
 * Returns an object that describes a comparison for timestamp in a time range.
 * @param {Array.<number>} range Start and stop timestamp.
 * @returns {Object.<*>} Object that describes time comparison.
 */
rule_builder.rangeTimeComparison = function(range:number[]): Record<string, any> {
  if (!_.isArray(range)) {
    Logger.error('Rule builder', 'rangeTimeComparison passed invalid range', range);
    return null;
  }

  return {
    'op': 'between',
    'args': [{
      'field': [ behavior.FIELDS.TIME ]
    }, {
      'value': [
        range[0] || +new Date(0),
        range[1] || +new Date()
      ]
    }]
  };
};

/**
 * Returns an object that describes groupBy fields.
 * @param {Array.<Array.<string>>} fields Fields to group by.
 * @returns {Array.<Object.<*>>} Array with objects that describes groupBy fields.
 */
rule_builder.groupBy = function(fields:string[][]): Record<string, any>[] {
  var groupByFields = [];
  for (var i = 0; i < fields.length; i++) {
    groupByFields[i] = {
      'field': fields[i]
    };
  }
  return groupByFields;
};

/**
 * Returns an object that describes an aggregator on the field.
 * @param {string} aggregateOp Aggregate operator.
 * @param {(string|Array.<string>)} [field] Field descriptor.
 * @returns {Object.<*>} Object that describes aggregator.
 */
rule_builder.aggregate = function(aggregateOp:string, field?:string|string[]): Record<string, any> {
  if (_.isString(field)) {
    field = [ field as string ];
  }
  field = field || rules.DEFAULT_FIELD;
  return {
    'op': aggregateOp,
    'args': [{
      'field': field
    }]
  };
};

/**
 * Source types for events.
 * @type {Object.<string>}
 */
rule_builder.SOURCE_TYPES = {
  BEHAVIOR: 'events',
  CUSTOM_BEHAVIOR: 'custom_behavior',
  DCP: 'dcp',
};

/**
 * Frequency filters.
 * @type {Object.<string>}
 */
rule_builder.FREQUENCY_FILTERS = {
  MOST_FREQUENT: 'most_frequent',
  LEAST_FREQUENT: 'least_frequent',
};

/**
 * Recency filters.
 * @type {Object.<string>}
 */
rule_builder.RECENCY_FILTERS = {
  MOST_RECENT: 'most_recent',
  LEAST_RECENT: 'least_recent',
};

/**
 * Returns source specific key-path of field name.
 * @param {string|Array.<string>} fieldName Name of field.
 * @param {string} [source] Optional source of events.
 * @returns {Array.<string>} Key-path of field name.
 */
rule_builder.getFieldKeyPathForSource = function(fieldName:string|string[], source?:string): string[] {
  source = source || rule_builder.SOURCE_TYPES.BEHAVIOR;
  var fieldKeyPath:string[] = [];
  if (_.isString(fieldName)) {
    fieldKeyPath = [ fieldName as string ];
    if (source === rule_builder.SOURCE_TYPES.BEHAVIOR &&
      !_.includes(_.values(behavior.FIELDS), fieldName)) {
      // Behavioral events tags are stored under the OPTIONS field name.
      fieldKeyPath = [ behavior.FIELDS.OPTIONS, fieldName ];
    }
  } else {
    // Must be an array of strings.
    fieldKeyPath = fieldName as string[];
  }

  return fieldKeyPath;
};

/**
 * Returns rule that picks n most recent events or n most frequent values of a
 * specified field.
 * @param {Object} pickSpec
 * @param {Object} filterClause Filter to apply before picking.
 * @param {string} [source] Optional source of events.
 * @returns {RuleSpec} Rule to pick events or values.
 * @private
 */
function buildPicker(pickSpec:any, filterClause:any, source?:string): RuleSpec {
  var picker = {
    'where': filterClause,
  };
  if (pickSpec.count) {
    picker['limit'] = pickSpec.count;
  }

  if (pickSpec.modifier === rule_builder.FREQUENCY_FILTERS.MOST_FREQUENT) {
    var fieldToGroupBy = rule_builder.getFieldKeyPathForSource(pickSpec.name, source);
    var aggregator = rule_builder.aggregate('count');
    var aggregateField = rule_builder.aggregateField('count');
    var selectField = rule_builder.groupField(fieldToGroupBy);

    return _.extend(picker, {
      'select': [{
        'field': selectField,
      }],
      'groupBy': rule_builder.groupBy([ fieldToGroupBy ]),
      'aggregate': [ aggregator ],
      'orderBy': [{
        'field': aggregateField,
        'direction': 'DESC',
      }]
    });
  }

  // Defaults to 'most_recent'.
  return _.extend(picker, {
    'orderBy': [{
      'field': [ behavior.FIELDS.TIME ],
      'direction': 'DESC',
    }],
  });
}

/**
 * Returns rules built from high-level rule format v0.1.
 * @param {RuleSpecV0_1} btAudienceSpec
 * @return {Array.<RuleSpec>} Rules built from ruleSpec.
 */
rule_builder.buildFromSpecV0_1 = function(btAudienceSpec:RuleSpecV0_1): RuleSpec[] {
  if (__INVARIANT__) {
    require('core/utils/invariant').isType('RuleSpecV0_1', btAudienceSpec);
  }

  // There must be an action field or some filters.
  if (!btAudienceSpec.action && (!btAudienceSpec.filters || btAudienceSpec.filters.length === 0)) {
    throw new Error('Audience spec must have an "action" field or at least one "filter" ' +
      JSONEnv.stringify(btAudienceSpec));
  }

  var filterClause = rule_builder.fieldComparison('gt', behavior.FIELDS.TIME, 0);  // Default filter.
  var aggregateRules = [];

  // Build the filterClause.
  var filterSubClauses = [];
  if (btAudienceSpec.action) {
    filterSubClauses.push(rule_builder.fieldComparison('eq', behavior.FIELDS.NAME, btAudienceSpec.action.value));
    if (btAudienceSpec.action.type) {
      filterSubClauses.push(rule_builder.fieldComparison('eq', behavior.FIELDS.TYPE, btAudienceSpec.action.type));
    }
  }

  // Time filter?
  if (btAudienceSpec.time) {
    if (btAudienceSpec.time.type === 'last_days') {
      filterSubClauses.push(rule_builder.relativeTimeComparison('lte', btAudienceSpec.time.days));
    } else if (btAudienceSpec.time.type === 'range') {
      var timeRangeFilter = rule_builder.rangeTimeComparison([btAudienceSpec.time.start, btAudienceSpec.time.stop]);
      if (timeRangeFilter) {
        filterSubClauses.push(timeRangeFilter);
      }
    } else {
      // Bad time type. Log and ignore.
      Logger.error('Rule builder', 'Audience spec has bad "time" type', btAudienceSpec.time.type);
    }
  }

  // Tag comparisons may add more filter sub-clauses; they will be processed last.
  filterClause = {
    'op': 'and',
    'args': filterSubClauses
  };

  // Is there a count clause?
  if (btAudienceSpec.count) {
    aggregateRules.push({
      // '0' is the first selected item in the 'from' clause.
      'where': rule_builder.fieldComparison(btAudienceSpec.count.comparator, '0', btAudienceSpec.count.value),
      'from': {
        'select': [{
          'field': rule_builder.aggregateField('count')
        }],
        'where': filterClause,
        'aggregate': [ rule_builder.aggregate('count') ],
      }
    });
  }

  // Process filters.
  if (btAudienceSpec.filters) {
    _.each(btAudienceSpec.filters, function(filter) {
      var fieldKeyPath = rule_builder.getFieldKeyPathForSource(filter.name, btAudienceSpec.source);
      var aggregator;
      var aggregateField;

      // Aggregate 'count' for MOST_FREQUENT, 'max' time for MOST_RECENT.
      // TODO(ajith): Handle LEAST_FREQUENT and LEAST_RECENT.
      if (filter.modifier === rule_builder.FREQUENCY_FILTERS.MOST_FREQUENT) {
        aggregator = rule_builder.aggregate('count');
        aggregateField = rule_builder.aggregateField('count');
      } else if (filter.modifier === rule_builder.RECENCY_FILTERS.MOST_RECENT) {
        aggregator = rule_builder.aggregate('max', behavior.FIELDS.TIME);
        aggregateField = rule_builder.aggregateField('max', behavior.FIELDS.TIME);
      }

      if (aggregator) {
        var fieldToGroupBy = fieldKeyPath;
        var selectField = rule_builder.groupField(fieldToGroupBy);

        aggregateRules.push({
          // '0' is the first selected item in the 'from' clause.
          'where': rule_builder.fieldComparison(filter.comparator, '0', filter.value),
          'from': {
            'select': [{
              'field': selectField,
            }],
            'where': filterClause,
            'groupBy': rule_builder.groupBy([fieldToGroupBy]),
            'aggregate': [aggregator],
            'orderBy': [{
              'field': aggregateField,
              'direction': 'DESC',
            }],
            'limit': 1,
          }
        });
      } else {
        // Goes into filterSubClauses.
        filterSubClauses.push(
          rule_builder.fieldComparison(filter.comparator, fieldKeyPath, filter.value));
      }
    });
  }

  // Pick?
  // NOTE: The 'pick' clause cannot be combined with a 'count' clause or
  // filters that specify 'MOST_RECENT', 'MOST_FREQUENT' modifiers.
  if (btAudienceSpec.pick) {
    if (aggregateRules.length > 0) {
      throw new Error('A "pick" clause must not be specified with "count" or "most_recent", "most_frequent" modifiers' +
        JSONEnv.stringify(btAudienceSpec));
    }

    return [ buildPicker(btAudienceSpec.pick, filterClause, btAudienceSpec.source) ];
  }

  if (aggregateRules.length > 0) {
    return aggregateRules;
  }

  return [{
    'where': filterClause,
  }];
};

/**
 * Executes a spec in high-level rule format v0.2.
 * @param {RuleSpecV0_2} ruleSpec
 * @return {RuleSpec} Rule built from ruleSpec.
 */
rule_builder.buildFromSpecV0_2 = function(ruleSpec:RuleSpecV0_2): RuleSpec {
  validateSpecV0_2(ruleSpec);

  // Sanity check: make sure the type system agrees that we have valid input.
  // Do this *after* validation because it would generate inferior error messages when given invalid input.
  if (__INVARIANT__) {
    require('core/utils/invariant').isType('RuleSpecV0_2', ruleSpec);
  }

  var rule = {
    // We must always include a where clause because it's required by the type invariants.
    'where': {
      'op': 'and',
      'args': _.map(ruleSpec['filter'] || [], function (filter) {
        // TODO: _.isEqual(filter['field'], ['age'])
        if (filter['field'][0] === 'age') {
          return rule_builder.relativeTimeComparison(
            filter['comparator'] || 'eq',
            filter['value'] / rule_builder.MILLIS_IN_A_DAY
          );
        } else {
          return rule_builder.fieldComparison(
            filter['comparator'] || 'eq',
            rule_builder.convertFieldKeyPathFromSpecV0_2(filter['field']),
            filter['value']
          );
        }
      })
    },
  };

  if (ruleSpec['reduce'] && ruleSpec['reduce']['aggregator'] === 'count') {
    // Evaluates to a list of a single list of a single value, so we should return without attempting to perform any
    // other operations.
    return _.extend(rule, {
      'aggregate': [{
        'op': 'count',
        'args': [{
          'field': ['*']
        }],
      }],
      'select': [{
        'field': ['_count_*']
      }],
    });
  }

  var frequencySorts = [];
  var nonFrequencySorts = [];
  if (ruleSpec['sort']) {
    _.each(ruleSpec['sort'], function (sort) {
      if (_.includes(['ascending', 'descending'], sort['direction'])) {
        // TODO: Allow for sorting by more non-frequency fields.
        // TODO: _.some([['time']], _.partial(_.isEqual, sort['field'))
        if (_.includes(['time', 'age'], sort['field'][0])) {
          nonFrequencySorts.push(sort);
        }
        // TODO: _.isEqual(sort['field'], ['frequency'])
        if (sort['field'][0] === 'frequency') {
          frequencySorts.push(sort);
        }
      }
    });
    if (nonFrequencySorts.length && !frequencySorts.length) {
      rule['orderBy'] = _.filter(_.map(nonFrequencySorts, function (sort) {
        if (sort['field'][0] === 'time') {
          return {
            'field': rule_builder.convertFieldKeyPathFromSpecV0_2(['time']),
            'direction': sort['direction'] === 'ascending' ? 'ASC' : 'DESC'
          };
        } else if (sort['field'][0] === 'age') {
          return {
            // Interpret this as a sort on the ['time'] field...
            'field': rule_builder.convertFieldKeyPathFromSpecV0_2(['time']),
            // ...but in the opposite direction.
            'direction': sort['direction'] === 'ascending' ? 'DESC' : 'ASC'
          };
        }
      }));
    }
  }

  if (ruleSpec['pick'] && ruleSpec['pick']['field']) {
    var pickField = rule_builder.convertFieldKeyPathFromSpecV0_2(ruleSpec['pick']['field']);

    if (ruleSpec['reduce'] && _.includes(['avg', 'max', 'min', 'sum'], ruleSpec['reduce']['aggregator'])) {
      // Evaluates to a list of a single list of a single value, so we should return without attempting to perform any
      // other operations.
      return _.extend(rule, {
        'aggregate': [{
          'op': ruleSpec['reduce']['aggregator'],
          'args': [{
            'field': pickField
          }]
        }],
        'select': [{
          'field': [rules.generateAlias(ruleSpec['reduce']['aggregator'], pickField)]
        }],
      });
    }

    // Evaluate to a list of lists that each contain a single value.
    if (frequencySorts.length) {
      rule = _.extend(rule, {
        'groupBy': [{
          'field': pickField
        }],
        'aggregate': [{
          'op': 'count',
          'args': [{
            'field': ['*']
          }]
        }],
        'orderBy': [{
          'field': ['_count_*'],
          'direction': frequencySorts[0]['direction'] === 'ascending' ? 'ASC' : 'DESC'
        }],
        'select': [{
          // groupBy generates *period-delimited field strings* instead of field arrays. :/
          'field': [pickField.join('.')]
        }],
      });
    } else {
      // Non-frequency sort orders may have been applied in this case.
      rule = _.extend(rule, {
        'select': [{
          'field': pickField
        }],
      });
    }
  }

  if (ruleSpec['reduce'] && ruleSpec['reduce']['aggregator'] === 'nth') {
    var n = ruleSpec['reduce']['n'];
    if (_.isNumber(n) && n >= 0 && Number(n) === Math.floor(Number(n))) {
      // Evaluates to a list of a single list of a single value, so we should return without attempting to perform any
      // other operations.
      return _.extend(rule, {
        'offset': n,
        'limit': 1
      });
    }
  }

  return rule;
};

/**
 * Translate fields' key-paths from the documented, human-readable ones to the internal ones.
 *
 * Examples:
 * - ['name'] -> ['n']
 * - ['tags', 'revenue'] -> ['r'] because revenue is stored as ['r'] on events but is accessible via the 'revenue' tag
 *   throughout our JS APIs.
 * - ['tags', 'color'] -> ['o', 'color'].
 *
 * TODO(nikhil): Add support for non-behavior sources?
 *
 * @private
 * @param {RuleSpecFieldV0_2} field
 * @returns {Array.<string>} Normalized key-path.
 */
rule_builder.convertFieldKeyPathFromSpecV0_2 = function(field:RuleSpecFieldV0_2): string[] {
  // TODO: _.isEqual(field, ['tags', 'revenue'])
  if (field[0] === 'tags' && field[1] === 'revenue') {
    return ['r'];
  } else {
    return [behavior.FIELDS_V0_2[field[0]]].concat(field.slice(1));
  }
};

/**
 * Validates a spec in high-level rule format v0.2.
 * @private
 * @param {RuleSpecV0_2} ruleSpec
 * @throws {Error} if the input is invalid or suggests confusion over semantics
 */
function validateSpecV0_2(ruleSpec:RuleSpecV0_2) {
  var errors = [];

  if (_.isUndefined(ruleSpec)) {
    throw new Error('rule is undefined');
  } else if (!_.isObject(ruleSpec)) {
    throw new Error('rule is not an Object');
  }

  if (ruleSpec['version'] !== '0.2') {
    errors.push('version: not "0.2"');
  }

  if (ruleSpec['filter']) {
    if (_.isArray(ruleSpec['filter'])) {
      _.each(ruleSpec['filter'], function(filter, i) {
        var filterFieldError = rule_builder.validateFieldKeyPathV0_2(filter['field'], rule_builder.FieldPurpose.FILTER);
        if (filterFieldError) {
          errors.push('filter[' + i + ']: ' + filterFieldError);
        }
        var comparisonAndValueError = rule_builder.validateComparatorAndValue(filter['comparator'], filter['value']);
        if (comparisonAndValueError) {
          errors.push('filter[' + i + ']: ' + comparisonAndValueError);
        }
      });
    } else {
      errors.push('filter: not an array');
    }
  }

  var frequencySorts = [];
  var nonFrequencySorts = [];
  if (ruleSpec['sort']) {
    if (ruleSpec['reduce'] && ruleSpec['reduce']['aggregator'] && ruleSpec['reduce']['aggregator'] !== 'nth') {
      errors.push('sort: superfluous because we can apply aggregator ' + JSONEnv.stringify(ruleSpec['reduce']['aggregator']) + ' to unsorted items');
    }

    if (_.isArray(ruleSpec['sort'])) {
      _.each(ruleSpec['sort'], function(sort, i) {
        var sortFieldError = rule_builder.validateFieldKeyPathV0_2(sort['field'], rule_builder.FieldPurpose.SORT);
        if (sortFieldError) {
          errors.push('sort[' + i + ']: ' + sortFieldError);
        }
        // TODO: _.isEqual(sort['field'], ['frequency'])
        if (sort['field'] && sort['field'][0] === 'frequency') {
          frequencySorts.push(sort);
        } else {
          nonFrequencySorts.push(sort);
        }
        var directionError = validateSortDirection(sort['direction']);
        if (directionError) {
          errors.push('sort[' + i + ']: ' + directionError);
        }
      });
    } else {
      errors.push('sort: not an array');
    }

    if (frequencySorts.length && nonFrequencySorts.length) {
      errors.push('sort: sorting by non-["frequency"] field is pointless because we are going to sort the picked values by ["frequency"]');
    }
    if (frequencySorts.length && !ruleSpec['pick']) {
      errors.push('sort: sorting by ["frequency"] is impossible because no values have been picked');
    }
  }

  if (ruleSpec['pick']) {
    if (ruleSpec['reduce'] && ruleSpec['reduce']['aggregator'] === 'count') {
      errors.push('pick: superfluous because we can apply aggregator "count" to raw events');
    }

    var fieldError = rule_builder.validateFieldKeyPathV0_2(ruleSpec['pick']['field']);
    if (fieldError) {
      errors.push('pick: ' + fieldError);
    }
  }

  if (ruleSpec['reduce']) {
    var aggregator = ruleSpec['reduce']['aggregator'];
    var aggregatorDescriptor = 'aggregator ' + (JSONEnv.stringify(aggregator) || String(aggregator));
    var n = ruleSpec['reduce']['n'];
    var nDescriptor = 'index ' + (JSONEnv.stringify(n) || String(n));

    if (!_.includes(['sum', 'avg', 'max', 'min', 'count', 'nth'], aggregator)) {
      errors.push('reduce: ' + aggregatorDescriptor + ' is unknown');
    }

    if (_.includes(['sum', 'avg', 'max', 'min'], aggregator)) {
      if (!ruleSpec['pick']) {
        errors.push('reduce: ' + aggregatorDescriptor + ' is impossible to use because no values have been picked');
      }
    }

    if (aggregator === 'nth') {
      if (!_.isNumber(n) || isNaN(n) || parseInt(n, 10) !== n || n < 0) {
        errors.push('reduce: ' + nDescriptor + ' is not a non-negative integer (mandated by ' + aggregatorDescriptor + ')');
      }
      if (!ruleSpec['sort']) {
        errors.push('reduce: aggregator "nth" is meaningless without a specific sort order');
      }
    } else {
      if (!_.isUndefined(n)) {
        errors.push('reduce: ' + nDescriptor + ' is defined (not mandated by ' + aggregatorDescriptor + ')');
      }
    }
  }

  if (errors.length) {
    throw new Error(errors.join('\n'));
  }
}

/**
 * @private
 * @enum {string}
 */
rule_builder.FieldPurpose = {
  FILTER: 'filter',
  SORT: 'sort',
  PICK: 'pick'
};

/**
 * @private
 * @param {RuleSpecFieldV0_2} fieldV0_2
 * @param {rule_builder.FieldPurpose?} fieldPurpose
 * @returns {string?} error
 */
rule_builder.validateFieldKeyPathV0_2 = function(fieldV0_2:RuleSpecFieldV0_2, fieldPurpose:import('plugins/feature_behavior/helpers/rules').FieldPurpose): string {
  var fieldDescriptor = 'field ' + (JSONEnv.stringify(fieldV0_2) || String(fieldV0_2));

  if (!_.isArray(fieldV0_2) || !_.every(fieldV0_2, _.isString)) {
    return fieldDescriptor + ' is not an array of strings';
  }

  if ((fieldV0_2[0] === 'tags' && fieldV0_2.length > 2) ||
      (fieldV0_2[0] !== 'tags' && fieldV0_2.length > 1)) {
    return fieldDescriptor + ' includes too many strings';
  }

  if (fieldV0_2[0] === 'tags' && fieldV0_2.length < 2) {
    return fieldDescriptor + ' does not specify an exact tag';
  }

  if (fieldV0_2.length < 1) {
    return fieldDescriptor + ' does not specify a top-level field';
  }

  var supportedFields = _.keys(behavior.FIELDS_V0_2);
  var explicitlyUnsupportedFields = [
    'age',
    'frequency',
  ];
  if (fieldPurpose === rule_builder.FieldPurpose.FILTER) {
    supportedFields.push('age');
    explicitlyUnsupportedFields = ['frequency'];
  }
  if (fieldPurpose === rule_builder.FieldPurpose.SORT) {
    supportedFields = [
      'time',
      'age',
      'frequency',
    ];
    // TODO: _.difference(_.keys(behavior.FIELDS_V0_2), supportedFields)
    explicitlyUnsupportedFields = [
      'name',
      'type',
      'category',
      'tags',
    ];
  }

  if (_.includes(explicitlyUnsupportedFields, fieldV0_2[0])) {
    return fieldDescriptor + ' is not supported here';
  }

  if (!_.includes(supportedFields, fieldV0_2[0])) {
    return fieldDescriptor + ' is unknown';
  }
};

/**
 * @private
 * @param {string?} comparator
 * @param {*} value
 * @returns {string?} error
 */
rule_builder.validateComparatorAndValue = function(comparator:string, value:any): string {
  var comparatorDescriptor = 'comparator ' + (JSONEnv.stringify(comparator) || String(comparator));
  var valueDescriptor = 'value ' + (JSONEnv.stringify(value) || String(value));

  if (!_.isString(comparator) && !_.isUndefined(comparator)) {
    return comparatorDescriptor + ' is not a string';
  }

  switch (comparator) {
    case undefined:
    case 'eq':
    case 'is':
    case 'contains':
      break;

    case 'lt':
    case 'gt':
    case 'lte':
    case 'gte':
      if (!_.isNumber(value)) {
        return valueDescriptor + ' is not a number (mandated by ' + comparatorDescriptor + ')';
      }
      break;

    case 'in':
      if (!_.isArray(value)) {
        return valueDescriptor + ' is not an array (mandated by ' + comparatorDescriptor + ')';
      }
      break;

    case 'between':
      if (!(_.isArray(value) && value.length === 2 && _.isNumber(value[0]) && _.isNumber(value[1]) && value[0] <= value[1])) {
        return valueDescriptor + ' is not a pair of increasing numbers (mandated by ' + comparatorDescriptor + ')';
      }
      break;

    case 'regex':
      if (!(_.isString(value) ||
            (_.isArray(value) && value.length === 2 && _.isString(value[0]) && _.isString(value[1])))) {
        return valueDescriptor + ' is not a pattern string or a [pattern string, flags string] array (mandated by ' + comparatorDescriptor + ')';
      }
      break;

    case 'exists':
      if (!_.isUndefined(value)) {
        return valueDescriptor + ' is not undefined (mandated by ' + comparatorDescriptor + ')';
      }
      break;

    default:
      return comparatorDescriptor + ' is unknown';
  }
};

/**
 * @private
 * @param {string} direction
 * @returns {string?} error
 */
function validateSortDirection(direction:string): string {
  var directionDescriptor = 'direction ' + (JSONEnv.stringify(direction) || String(direction));
  if (!_.includes(['ascending', 'descending'], direction)) {
    return directionDescriptor + ' is not "ascending" or "descending"';
  }
}
